/**
 * Font Manager
 * 
 * A wrapper around the font-loader utility that provides a convenient API
 * for font loading in the application.
 * 
 * Enhanced with:
 * - Detailed font loading status information
 * - Performance metrics
 * - Event handling for font loading states
 * - Debug mode for diagnostics
 * - LocalStorage caching of successful font source
 */

import { 
  loadFonts, 
  setDebugMode, 
  getActiveFontSource, 
  getFontLoadingTimings,
  setActiveSource,
  injectSelfHostedFontDefinitions
} from './font-loader';

/**
 * Manages font loading and provides a convenient API for the application
 */
export class FontManager {
  // Store the promise for external code to await
  private fontsLoadedPromise: Promise<boolean> | null = null;
  
  // Debug mode status
  private _debugMode = false;
  
  // Track initialization state
  private _initialized = false;
  
  /**
   * Constructor - sets up the instance and optionally starts font loading
   * @param options Configuration options
   */
  constructor(options?: { 
    debugMode?: boolean; 
    forceSource?: 'google' | 'self-hosted';
    useCachedSource?: boolean;
    autoInit?: boolean;
  }) {
    // Check for global skip flag - allows early adopters to completely skip font loading
    // when they've handled it in inlined script already
    if (typeof window !== 'undefined' && (window as any).skipFontLoading) {
      if (options?.debugMode) {
        console.log('Font loading skipped due to skipFontLoading flag');
      }
      
      // Mark as initialized but don't actually load fonts
      this._initialized = true;
      
      // Set active source from window.cachedFontSource if available
      if ((window as any).cachedFontSource) {
        try {
          const cachedSource = (window as any).cachedFontSource;
          setActiveSource(cachedSource);
          
          if (cachedSource === 'self-hosted') {
            // We need to ensure self-hosted font definitions are injected
            injectSelfHostedFontDefinitions();
            if (options?.debugMode) {
              console.log('Injected self-hosted font definitions from cached source');
            }
          }
          
          if (options?.debugMode) {
            console.log(`Set active font source to ${cachedSource} (cached)`);
          }
        } catch (e) {
          // If this fails, we'll still have the correct active source locally
          if (options?.debugMode) {
            console.warn('Failed to set active source in font-loader module:', e);
          }
        }
      }
      
      return;
    }
    
    // Auto-initialize by default unless explicitly disabled
    const shouldAutoInit = options?.autoInit !== false;
    
    // If auto-init, initialize right away
    if (shouldAutoInit) {
      this.initialize(options);
    }
  }
  
  /**
   * Initialize the font manager and start loading fonts
   * @param options Configuration options
   * @returns The font manager instance for chaining
   */
  public initialize(options?: { 
    debugMode?: boolean; 
    forceSource?: 'google' | 'self-hosted';
    useCachedSource?: boolean;
  }): FontManager {
    // Prevent double initialization
    if (this._initialized) {
      if (this._debugMode) {
        console.warn('Font manager already initialized. Call reloadFonts() to reload fonts.');
      }
      return this;
    }
    
    this._initialized = true;
    
    // Set debug mode if requested
    if (options?.debugMode) {
      this._debugMode = true;
      setDebugMode(true);
    }
    
    // Check for cached font source if not explicitly overridden
    let sourceToUse = options?.forceSource;
    
    // Only try to use the cached source if useCachedSource is true or not explicitly specified
    if (!sourceToUse && (options?.useCachedSource || options?.useCachedSource === undefined)) {
      const cachedSource = this.getCachedFontSource();
      if (cachedSource && this._debugMode) {
        console.log(`Using cached font source: ${cachedSource}`);
      }
      sourceToUse = cachedSource;
    }
    
    // Initialize font loading with potentially cached source
    this.fontsLoadedPromise = this.loadFontsInternal(sourceToUse);
    
    // Set up event listeners
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      try {
        window.addEventListener('fontsLoaded', this.handleFontsLoaded.bind(this));
        window.addEventListener('fontsFailed', this.handleFontsFailed.bind(this));
      } catch (err) {
        console.error('Error setting up font event listeners:', err);
      }
    }
    
    return this;
  }
  
  /**
   * Internal method to load fonts and manage HTML classes
   * @param forceSource Optional parameter to force a specific font source
   * @returns Promise that resolves when fonts are loaded
   */
  private loadFontsInternal(forceSource?: 'google' | 'self-hosted'): Promise<boolean> {
    return loadFonts(forceSource);
  }
  
  /**
   * Returns a promise that resolves when fonts are loaded
   * @returns Promise that resolves with the font loading result (true if successful)
   */
  public onFontsLoaded(): Promise<boolean> {
    if (!this._initialized) {
      throw new Error('Font manager not initialized. Call initialize() first.');
    }
    
    // Special case: if we're initialized but skipped loading (due to skipFontLoading flag)
    // and we have an active source, we can resolve immediately with success
    if (typeof window !== 'undefined' && 
        (window as any).skipFontLoading && 
        getActiveFontSource()) {
      return Promise.resolve(true);
    }
    
    return this.fontsLoadedPromise || Promise.resolve(false);
  }
  
  /**
   * Handle the fontsLoaded event
   * @param event Custom event with font loading details
   */
  private handleFontsLoaded(event: Event): void {
    if (this._debugMode) {
      const customEvent = event as CustomEvent;
      console.log('Fonts loaded successfully:', customEvent.detail);
    }
  }
  
  /**
   * Handle the fontsFailed event
   * @param event Custom event with font loading details
   */
  private handleFontsFailed(event: Event): void {
    if (this._debugMode) {
      const customEvent = event as CustomEvent;
      console.error('Font loading failed:', customEvent.detail);
    }
  }
  
  /**
   * Enable or disable debug mode
   * @param enable Whether to enable debug mode
   */
  public setDebugMode(enable: boolean): void {
    this._debugMode = enable;
    setDebugMode(enable);
  }
  
  /**
   * Get the active font source
   * @returns The active font source
   */
  public getActiveFontSource(): 'google' | 'self-hosted' | null {
    return getActiveFontSource();
  }
  
  /**
   * Get font loading performance timings
   * @returns Object with performance timings in milliseconds
   */
  public getPerformanceTimings() {
    return getFontLoadingTimings();
  }
  
  /**
   * Force reload fonts from a specific source
   * Useful for testing or recovering from font loading issues
   * @param source The source to load fonts from
   * @param useCachedSource Whether to use the cached source if available
   * @returns Promise that resolves when fonts are loaded
   */
  public reloadFonts(source?: 'google' | 'self-hosted', useCachedSource?: boolean): Promise<boolean> {
    if (!this._initialized) {
      console.warn('Font manager not initialized. Initializing now...');
      return this.initialize({ 
        debugMode: this._debugMode, 
        forceSource: source,
        useCachedSource: useCachedSource
      }).onFontsLoaded();
    }
    
    // If useCachedSource is true and source is not specified, use the cached source
    let sourceToUse = source;
    if (!sourceToUse && useCachedSource) {
      sourceToUse = this.getActiveFontSource() || undefined;
      
      if (this._debugMode && sourceToUse) {
        console.log(`Using cached font source for reload: ${sourceToUse}`);
      }
    }
    
    if (this._debugMode) {
      console.log(`Manually reloading fonts${sourceToUse ? ` from ${sourceToUse}` : ''}`);
    }
    
    this.fontsLoadedPromise = this.loadFontsInternal(sourceToUse);
    return this.fontsLoadedPromise;
  }
  
  /**
   * Check if the font manager has been initialized
   * @returns True if the font manager has been initialized
   */
  public isInitialized(): boolean {
    return this._initialized;
  }
  
  /**
   * Get cached font data from localStorage
   * @returns The cached font source or undefined if not found
   */
  public getCachedFontSource(): 'google' | 'self-hosted' | undefined {
    if (typeof localStorage === 'undefined') return undefined;
    
    try {
      const fontData = localStorage.getItem('fontLoadData');
      if (fontData) {
        const data = JSON.parse(fontData);
        // Check if data is still valid (not expired)
        if (data && data.timestamp && (Date.now() - data.timestamp < 604800000)) { // 1 week
          return data.source as 'google' | 'self-hosted';
        }
      }
    } catch (e) {
      console.error('Error getting cached font source:', e);
    }
    
    return undefined;
  }
  
  /**
   * Invalidate the font cache
   * Useful when updating fonts or when font loading consistently fails
   */
  public invalidateFontCache(): void {
    if (typeof localStorage === 'undefined') return;
    
    try {
      localStorage.removeItem('fontLoadData');
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove('fonts-loaded');
        document.documentElement.classList.remove('fonts-google');
        document.documentElement.classList.remove('fonts-self-hosted');
      }
      
      if (this._debugMode) {
        console.log('Font cache invalidated');
      }
    } catch (e) {
      console.error('Error invalidating font cache:', e);
    }
  }
}

// Create and export a singleton instance for use throughout the app
export const fontManager = new FontManager();

// Export default for default import pattern
export default fontManager;

// Expose the font manager to the global window object
if (typeof window !== 'undefined') {
  // Use the actual instance (not spreading properties) to preserve the prototype chain
  (window as any).fontManager = fontManager;
  
  // Also expose the class for creating new instances if needed
  (window as any).FontManager = FontManager;
}